'use client';

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { usePathname } from 'next/navigation';
import { useUser } from '@clerk/nextjs';

import dynamic from 'next/dynamic';

const NexusChart = dynamic(() => import('./nexus/NexusChart'), {
  ssr: false,
  loading: () => <div className="w-full h-64 animate-pulse bg-white/5 rounded-lg border border-white/10 flex items-center justify-center text-zinc-500 font-mono text-xs tracking-widest">INITIALIZING DATA CORE...</div>
});

const FeedbackForm = dynamic(() => import('./nexus/FeedbackForm'), { ssr: false });

const AI_THOUGHTS = [
  "Scanning 10,420 global institutional data nodes...",
  "Filtering political noise & irrelevant chatter...",
  "Cross-referencing sovereign economic & predictive models...",
  "Applying WPP Zero-Tolerance Filter...",
  "Running predictive geopolitical threat matrices...",
  "Synthesizing high-value market alpha...",
  "Generating 0.1% Actionable Intelligence...",
  "Encrypting output stream..."
];

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  latency?: number;
  isDataRoomUnlocked?: boolean;
  threatScan?: {
    target: string;
    level: string;
    summary: string;
  };
  ticketStatus?: {
    status: string;
    issue: string;
  };
  historyData?: any[];
  pdfReady?: boolean;
  pdfTitle?: string;
  file?: {
    data: string;
    mimeType: string;
    name: string;
  };
}

export default function WPPNexus() {
  const { isSignedIn, user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageContext, setPageContext] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [vaultSessions, setVaultSessions] = useState<any[]>([]);
  const [attachedFile, setAttachedFile] = useState<{data: string, mimeType: string, name: string} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef<Message[] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load Neural Cache
  useEffect(() => {
    try {
      const cached = localStorage.getItem('wpp_nexus_cache');
      if (cached) {
        setMessages(JSON.parse(cached));
      }
    } catch (e) {
      console.error('Neural cache corrupted');
    }
  }, []);

  // Save Neural Cache
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('wpp_nexus_cache', JSON.stringify(messages));
    } else {
      localStorage.removeItem('wpp_nexus_cache');
    }
  }, [messages]);

  // Global Hotkeys (Ctrl+K to toggle, Esc to close)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clock Update
  useEffect(() => {
    setCurrentTime(new Date());
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // AI Thought Stream logic
  const [currentThoughtIndex, setCurrentThoughtIndex] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading) {
      interval = setInterval(() => {
        setCurrentThoughtIndex((prev) => (prev + 1) % AI_THOUGHTS.length);
      }, 800);
    } else {
      setCurrentThoughtIndex(0);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Voice Input (Jarvis Mode)
  const handleVoiceInput = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("VOICE_PROTOCOL_ERROR: Speech recognition is not supported or permitted in this browser instance.");
      return;
    }
    
    if (isListening) return;
    
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev + (prev ? ' ' : '') + transcript);
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  // Helper to get current time
  const getTime = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Load chat history on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('nexus_chat_history');
      if (saved) {
        setMessages(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load chat history', e);
    }
  }, []);

  // Save chat history when updated
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('nexus_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  // Update page context when user navigates
  useEffect(() => {
    setPageContext('');
  }, [pathname]);

  // Scroll to bottom of chat when new messages appear
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  // Jump to bottom immediately on session change
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, 50);
    return () => clearTimeout(timer);
  }, [currentSessionId]);

  // Extract page context when opened or when page changes
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const title = document.title;
        const articleElement = document.querySelector('.prose') as HTMLElement;
        const articleContent = articleElement ? articleElement.innerText : ((document.querySelector('article') as HTMLElement)?.innerText || '');
        const deepScanContent = articleContent.substring(0, 50000);
        
        setPageContext(`Title: ${title}\n\nFull Article Content for Deep Analysis:\n${deepScanContent}`);
        
        // Add welcome message if chat is empty
        setMessages(prev => {
          if (prev.length === 0) {
            return [{
              role: 'assistant',
              content: "**[ SECURE CONNECTION ESTABLISHED ]**\n\nI am **WPP Nexus**, the autonomous intelligence agent for World Prime Post.\n\nSelect an operation protocol below or enter a custom query.",
              timestamp: getTime()
            }];
          }
          return prev;
        });
      }, 500); 
    }
  }, [isOpen, pathname]);

  const handleSubmit = async (e?: React.FormEvent, customInput?: string, forceNewChat?: boolean) => {
    if (e) e.preventDefault();
    if (!isSignedIn) {
      alert("Authentication required. Please log in to access the WPP Nexus Terminal.");
      return;
    }

    const submitText = customInput || input;
    if (!submitText.trim() || isLoading) return;

    let baseMessages = messages;
    if (forceNewChat && messages.length > 1) {
      // Clear current chat to start a new one for the protocol
      setCurrentSessionId(null);
      currentSessionIdRef.current = null;
      baseMessages = [
        {
          role: 'assistant',
          content: "**[ SECURE CONNECTION ESTABLISHED ]**\n\nI am **WPP Nexus**, the autonomous intelligence agent for World Prime Post.\n\nSelect an operation protocol below or enter a custom query.",
          timestamp: getTime()
        }
      ];
      setMessages(baseMessages);
    }

    const userMessage = { 
      role: 'user' as const, 
      content: submitText, 
      timestamp: getTime(),
      file: attachedFile ? { ...attachedFile } : undefined
    };
    setMessages(prev => forceNewChat ? [...baseMessages, userMessage] : [...prev, userMessage]);
    setInput('');
    const fileToUpload = attachedFile;
    setAttachedFile(null); // clear file immediately after submit
    setIsLoading(true);

    setMessages(prev => forceNewChat ? [...baseMessages, userMessage, { role: 'assistant', content: '', timestamp: getTime() }] : [...prev, { role: 'assistant', content: '', timestamp: getTime() }]);

    const startTime = Date.now();
    let finalAssistantText = '';
    let hasApiError = false;

    // Extract last 10 messages from vault to use as memory context
    let memoryContext = '';
    if (vaultSessions.length > 0) {
      try {
        const pastMsgs = [];
        // Get up to 2 past sessions to build a light memory profile
        for (let i = 0; i < Math.min(2, vaultSessions.length); i++) {
          const session = vaultSessions[i];
          const msgs = typeof session.messages === 'string' ? JSON.parse(session.messages) : session.messages;
          if (Array.isArray(msgs)) {
            pastMsgs.push(...msgs.filter((m: any) => m.role === 'user' || m.role === 'assistant').slice(-5));
          }
        }
        if (pastMsgs.length > 0) {
          memoryContext = pastMsgs.map((m: any) => `${m.role}: ${m.content}`).join('\n');
        }
      } catch (e) {
        console.error('Failed to parse memory context', e);
      }
    }

    try {
      const response = await fetch('/api/nexus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: forceNewChat ? [...baseMessages, userMessage] : [...messages, userMessage],
          context: pageContext,
          memoryContext: memoryContext,
          fileData: fileToUpload
        }),
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            const latencyMs = Date.now() - startTime;
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1].latency = latencyMs;
              
              if (finalAssistantText.includes('[[EXECUTE_INVESTOR_DATA_ROOM')) {
                newMessages[newMessages.length - 1].isDataRoomUnlocked = true;
              }
              
              const threatMatch = finalAssistantText.match(/\[\[RENDER_THREAT_SCAN target="(.*?)" level="(.*?)" summary="(.*?)"\]\]/);
              if (threatMatch) {
                newMessages[newMessages.length - 1].threatScan = {
                  target: threatMatch[1],
                  level: threatMatch[2],
                  summary: threatMatch[3]
                };
              }
              
              const pdfMatch = finalAssistantText.match(/\[\[DOWNLOAD_PDF title="(.*?)"\]\]/);
              if (pdfMatch) {
                newMessages[newMessages.length - 1].pdfReady = true;
                newMessages[newMessages.length - 1].pdfTitle = pdfMatch[1];
              }

              return newMessages;
            });

            // STREAM COMPLETE: Execute backend actions if tags are present
            try {
              const reqHeaders = { 'Content-Type': 'application/json' };

              const subMatch = finalAssistantText.match(/\[\[EXECUTE_SUBSCRIBE email="(.*?)"\]\]/);
              if (subMatch) {
                await fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'EXECUTE_SUBSCRIBE', payload: { email: subMatch[1] } })
                });
              }

              const supMatch = finalAssistantText.match(/\[\[EXECUTE_SUPPORT email="(.*?)" issue="(.*?)"\]\]/);
              if (supMatch) {
                const contextStr = messages.slice(-4, -1).map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
                await fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'EXECUTE_SUPPORT', payload: { email: supMatch[1], issue: supMatch[2], chatContext: contextStr } })
                });
              }

              const trackMatch = finalAssistantText.match(/\[\[TRACK_TICKET id="(.*?)"\]\]/);
              if (trackMatch) {
                fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'TRACK_TICKET', payload: { ticketId: trackMatch[1] } })
                }).then(res => res.json()).then(data => {
                  if (data.success) {
                    setMessages(prev => {
                      const newMsgs = [...prev];
                      newMsgs[newMsgs.length - 1].ticketStatus = { status: data.status, issue: data.issue };
                      return newMsgs;
                    });
                  }
                }).catch(e => console.error(e));
              }

              const historyMatch = finalAssistantText.match(/\[\[FETCH_USER_HISTORY\]\]/);
              if (historyMatch) {
                fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'FETCH_USER_HISTORY', payload: {} })
                }).then(res => res.json()).then(data => {
                  if (data.success) {
                    setMessages(prev => {
                      const newMsgs = [...prev];
                      if (Array.isArray(data.history)) {
                        newMsgs[newMsgs.length - 1].historyData = data.history;
                        newMsgs[newMsgs.length - 1].content = newMsgs[newMsgs.length - 1].content.replace('[[FETCH_USER_HISTORY]]', '');
                      } else {
                        const historyText = data.history.length > 0 ? `\n\n**Your Secure History:**\n\`\`\`\n${data.history}\n\`\`\`` : `\n\n*No historical records found for your account.*`;
                        newMsgs[newMsgs.length - 1].content = newMsgs[newMsgs.length - 1].content.replace('[[FETCH_USER_HISTORY]]', historyText);
                      }
                      return newMsgs;
                    });
                  }
                }).catch(e => console.error(e));
              }

              const instMatch = finalAssistantText.match(/\[\[EXECUTE_INSTITUTIONAL fullName="(.*?)" org="(.*?)" email="(.*?)" mobile="(.*?)" location="(.*?)" inquiryType="(.*?)" message="([\s\S]*?)"\]\]/);
              if (instMatch) {
                const contextStr = messages.slice(-4, -1).map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
                await fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'EXECUTE_INSTITUTIONAL', payload: { fullName: instMatch[1], org: instMatch[2], email: instMatch[3], mobile: instMatch[4], location: instMatch[5], inquiryType: instMatch[6], message: instMatch[7], chatContext: contextStr } })
                });
              }

              const recMatch = finalAssistantText.match(/\[\[EXECUTE_RECRUITMENT name="(.*?)" email="(.*?)" mobile="(.*?)" address="(.*?)" city="(.*?)" state="(.*?)" country="(.*?)"\]\]/);
              if (recMatch) {
                const contextStr = messages.slice(-4, -1).map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
                await fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'EXECUTE_RECRUITMENT', payload: { name: recMatch[1], email: recMatch[2], mobile: recMatch[3], address: recMatch[4], city: recMatch[5], state: recMatch[6], country: recMatch[7], chatContext: contextStr } })
                });
              }

              const b2bMatch = finalAssistantText.match(/\[\[EXECUTE_B2B_LEAD fullName="(.*?)" email="(.*?)" company="(.*?)" useCase="(.*?)" volume="(.*?)" queries="([\s\S]*?)"\]\]/);
              if (b2bMatch) {
                const contextStr = messages.slice(-4, -1).map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
                await fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'EXECUTE_B2B_LEAD', payload: { fullName: b2bMatch[1], email: b2bMatch[2], company: b2bMatch[3], useCase: b2bMatch[4], volume: b2bMatch[5], queries: b2bMatch[6], chatContext: contextStr } })
                });
              }

              const invMatch = finalAssistantText.match(/\[\[EXECUTE_INVESTOR_DATA_ROOM fullName="(.*?)" email="(.*?)" firm="(.*?)" linkedin="(.*?)" queries="([\s\S]*?)"\]\]/);
              if (invMatch) {
                const contextStr = messages.slice(-4, -1).map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
                await fetch('/api/nexus/action', {
                  method: 'POST',
                  headers: reqHeaders,
                  body: JSON.stringify({ action: 'EXECUTE_INVESTOR_DATA_ROOM', payload: { fullName: invMatch[1], email: invMatch[2], firm: invMatch[3], linkedin: invMatch[4], queries: invMatch[5], chatContext: contextStr } })
                });
              }

            } catch (err) {
              console.error("Action execution failed", err);
            }
            break;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          finalAssistantText += chunk;
          
          setMessages(prev => {
            const newMessages = [...prev];
            const lastIdx = newMessages.length - 1;
            newMessages[lastIdx] = {
              ...newMessages[lastIdx],
              content: newMessages[lastIdx].content + chunk
            };
            return newMessages;
          });
        }
      }
    } catch (error) {
      console.error("Chat Error:", error);
      hasApiError = true;
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1].content = '[WPP_NETWORK_ALERT]: Connection to global intelligence nodes temporarily constrained due to high bandwidth volume. Please stand by or upgrade your Enterprise Tier to expand protocol quota.';
        return newMessages;
      });
    } finally {
      if (finalAssistantText.trim() === '' && !hasApiError) {
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].content = '[WPP_SENTINEL_ALERT]: Query blocked. Geopolitical sensitivity or safety filter triggered. No data extracted.';
          return newMessages;
        });
      }
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: "**[ SECURE CONNECTION ESTABLISHED ]**\n\nI am **WPP Nexus**, the autonomous intelligence agent for World Prime Post.\n\nSelect an operation protocol below or enter a custom query.",
        timestamp: getTime()
      }
    ]);
  };

  const quickActions = [
    { title: "Join The Pulse", desc: "Global Intelligence Newsletter", icon: "🗞️", query: "I want to subscribe to The Pulse newsletter to receive global intelligence updates." },
    { title: "Join Our Mission", desc: "Intelligence Bureau Recruitment", icon: "💼", query: "I want to join your mission. Start the recruitment application protocol." },
    { title: "Project Vanguard", desc: "VC & Investor Data Room", icon: "💎", query: "I represent an investment firm and would like to request access to the Project Vanguard Data Room." },
    { title: "B2B API Solutions", desc: "Enterprise Data Feeds", icon: "🔌", query: "My company wants to integrate WPP Data Feeds and AI via API. How do we start?" },
    { title: "Live Market Data", desc: "Real-time Asset Tracking", icon: "💹", query: "Can I get the live market data and trend forecast?" },
    { title: "Global Risk Scan", desc: "Proprietary Threat Analysis", icon: "🌍", query: "Run a proprietary WPP Global Sentinel threat scan on the Middle East geopolitical situation." },
    { title: "My Request History", desc: "Track Past Tickets & Inquiries", icon: "📜", query: "Fetch the secure history of all my past requests, tickets, and institutional inquiries." },
    { title: "Support Desk", desc: "Technical & Platform Help", icon: "🛠️", query: "I need help. Can I raise a support ticket?" },
    { title: "Institutional Relations", desc: "Founder & Business Escalation", icon: "🏛️", query: "I want to contact the founder for Institutional Relations or business inquiries. Escalate this." },
    { title: "Alpha Signal", desc: "Under-reported Market Trends", icon: "📡", query: "Activate Alpha Signal Protocol. Scan internal WPP intelligence for under-reported, high-impact market trends." },
    { title: "Sentinel Matrix", desc: "T+36 Month Macro Forecast", icon: "🧠", query: "Initialize the Sentinel Predictive Matrix. Forecast global macroeconomic and technological shifts over the next 36 months to identify hidden Alpha." }
  ];

  // Helper to render cleaned text
  const cleanMessageText = (text: string) => {
    return text
      .replace(/\[\[EXECUTE_SUBSCRIBE.*?\]\]/g, '')
      .replace(/\[\[EXECUTE_SUPPORT.*?\]\]/g, '')
      .replace(/\[\[EXECUTE_INSTITUTIONAL.*?\]\]/g, '')
      .replace(/\[\[EXECUTE_RECRUITMENT.*?\]\]/g, '')
      .replace(/\[\[EXECUTE_B2B_LEAD.*?\]\]/g, '')
      .replace(/\[\[EXECUTE_INVESTOR_DATA_ROOM[\s\S]*?\]\]/g, '')
      .replace(/\[\[RENDER_THREAT_SCAN.*?\]\]/g, '')
      .replace(/\[\[DOWNLOAD_PDF.*?\]\]/g, '')
      .replace(/\[\[SHOW_MARKET_OPTIONS\]\]/g, '')
      .replace(/\[\[SHOW_SATISFACTION\]\]/g, '')
      .replace(/\[\[FETCH_USER_HISTORY\]\]/g, '')
      .replace(/\[\[TRACK_TICKET.*?\]\]/g, '')
      .replace(/\[\[SHOW_PROTOCOL_OPTIONS.*?\]\]/g, '')
      .replace(/\[\[RENDER_FEEDBACK_FORM\]\]/g, '');
  };

  // Keep ref in sync
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  const autoSaveToVault = async (currentMsgs: Message[]) => {
    if (!isSignedIn || currentMsgs.length < 2) return; // Don't save empty/initial chats

    if (isSavingRef.current) {
      pendingSaveRef.current = currentMsgs;
      return;
    }

    isSavingRef.current = true;
    try {
      // Auto-generate title based on user's first query if it's a new session
      let generatedTitle = `Session - ${new Date().toLocaleDateString()}`;
      let detectedProtocol = 'General Intelligence';

      const userQueries = currentMsgs.filter(m => m.role === 'user');
      if (userQueries.length > 0) {
        const firstQ = userQueries[0].content;
        const firstMsgText = firstQ.toLowerCase();

        if (firstMsgText.includes('/pulse') || firstMsgText.includes('subscribe to the pulse')) {
          detectedProtocol = 'The Pulse';
          generatedTitle = 'Pulse Intelligence Subscription';
        } else if (firstMsgText.includes('/mission') || firstMsgText.includes('recruitment application')) {
          detectedProtocol = 'Vanguard Protocol';
          generatedTitle = 'IB Recruitment Application';
        } else if (firstMsgText.includes('/vanguard') || firstMsgText.includes('vc & investor data room')) {
          detectedProtocol = 'Investor Data Room';
          generatedTitle = 'Vanguard Data Room Access';
        } else if (firstMsgText.includes('/api') || firstMsgText.includes('wpp data feeds and ai')) {
          detectedProtocol = 'B2B API Solutions';
          generatedTitle = 'B2B Enterprise API Integration';
        } else if (firstMsgText.includes('/market') || firstMsgText.includes('live market data')) {
          detectedProtocol = 'Live Market Data';
          generatedTitle = 'Market Trend Forecast';
        } else if (firstMsgText.includes('/risk') || firstMsgText.includes('global sentinel threat scan')) {
          detectedProtocol = 'Global Risk Scan';
          generatedTitle = 'Geopolitical Threat Scan';
        } else if (firstMsgText.includes('/history')) {
          detectedProtocol = 'Nexus Core';
          generatedTitle = 'Secure Request History';
        } else if (firstMsgText.includes('/support') || firstMsgText.includes('raise a support ticket')) {
          detectedProtocol = 'Support Desk';
          generatedTitle = 'Technical Support Ticket';
        } else if (firstMsgText.includes('feedback') || firstMsgText.includes('feedback for the product team')) {
          detectedProtocol = 'Feedback';
          generatedTitle = 'Nexus Feedback Uplink';
        } else if (firstMsgText.includes('/institutional') || firstMsgText.includes('contact the founder for institutional relations')) {
          detectedProtocol = 'Institutional Relations';
          generatedTitle = 'Founder & Business Escalation';
        } else if (firstMsgText.includes('/alpha') || firstMsgText.includes('activate alpha signal protocol')) {
          detectedProtocol = 'Alpha Signal';
          generatedTitle = 'Under-reported Market Trends';
        } else if (firstMsgText.includes('/sentinel') || firstMsgText.includes('initialize the sentinel predictive matrix')) {
          detectedProtocol = 'Sentinel Matrix';
          generatedTitle = 'T+36 Month Macro Forecast';
        } else {
          generatedTitle = firstQ.length > 30 ? firstQ.substring(0, 30) + '...' : firstQ;
        }
      }

      const res = await fetch('/api/nexus/vault/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: currentMsgs,
          title: generatedTitle,
          protocol: detectedProtocol,
          sessionId: currentSessionIdRef.current // Use ref to prevent stale closure
        }),
      });
      const data = await res.json();
      if (data.success && data.sessionId && data.sessionId !== currentSessionIdRef.current) {
        setCurrentSessionId(data.sessionId);
        currentSessionIdRef.current = data.sessionId; // Instantly update ref so pending save gets it
        fetchVaultSessions(); // Refresh list to show the new session
      }
    } catch (e) {
      console.error("Auto-save failed", e);
    } finally {
      isSavingRef.current = false;
      if (pendingSaveRef.current) {
        const nextMsgs = pendingSaveRef.current;
        pendingSaveRef.current = null;
        autoSaveToVault(nextMsgs);
      }
    }
  };

  // Trigger auto-save whenever messages array changes (even during AI streaming)
  useEffect(() => {
    if (messages.length > 1) {
      autoSaveToVault(messages);
    }
  }, [messages]);

  const fetchVaultSessions = async () => {
    if (!isSignedIn) return;
    try {
      const res = await fetch(`/api/nexus/vault/list?t=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      if (data.sessions) {
        setVaultSessions(data.sessions);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (isSignedIn) {
      fetchVaultSessions();
    }
  }, [isSignedIn]);

  const handleVaultAction = async (action: string, id: string, payload: any = {}) => {
    try {
      const res = await fetch('/api/nexus/vault/action', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: id, action, payload }),
      });
      if (res.ok) fetchVaultSessions();
    } catch (e) {
      console.error('Vault action error:', e);
    }
  };

  const loadSession = (session: any) => {
    try {
      let parsedMessages = [];
      if (typeof session.messages === 'string') {
        parsedMessages = JSON.parse(session.messages);
      } else if (Array.isArray(session.messages)) {
        parsedMessages = session.messages;
      }
      
      if (parsedMessages && parsedMessages.length > 0) {
        setMessages(parsedMessages);
        setCurrentSessionId(session._id);
        if (isMobile) setIsSidebarOpen(false);
      }
    } catch (e) {
      console.error('Failed to parse session messages', e);
      alert('Error loading session. Data might be corrupted.');
    }
  };

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(cleanMessageText(text));
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handlePrint = (targetId: string, title: string) => {
    const msgElement = document.getElementById(targetId);
    if (!msgElement) return;
    
    // Clone node to avoid modifying the actual UI
    const clone = msgElement.cloneNode(true) as HTMLElement;
    
    // Convert canvases to images in the clone
    const originalCanvases = Array.from(msgElement.getElementsByTagName('canvas'));
    const clonedCanvases = Array.from(clone.getElementsByTagName('canvas'));
    
    for (let i = 0; i < originalCanvases.length; i++) {
      try {
        const dataUrl = originalCanvases[i].toDataURL('image/png');
        const img = document.createElement('img');
        img.src = dataUrl;
        img.style.width = '100%';
        img.style.maxWidth = '600px';
        img.style.display = 'block';
        img.style.margin = '0 auto';
        clonedCanvases[i].parentNode?.replaceChild(img, clonedCanvases[i]);
      } catch(e) {
        console.error("Canvas export failed", e);
      }
    }
    
    // Remove download buttons from the PDF
    const downloadBtns = clone.querySelectorAll('.pdf-download-btn');
    downloadBtns.forEach(btn => btn.remove());
    
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              @page { margin: 25mm 20mm; }
              body { 
                background: #fff; 
                color: #000; 
                font-family: 'Inter', system-ui, sans-serif; 
                margin-bottom: 50px; 
              }
              svg { max-width: 100%; height: auto; }
              .prose { font-size: 13px; line-height: 1.6; }
              h1, h2, h3, h4 { color: #111; font-weight: 800; letter-spacing: -0.02em; }
              strong { color: #000; font-weight: bold; }
              .my-4 { margin: 24px 0; }
              .bg-black { background: #0a0a0a !important; color: #fff !important; border: 1px solid #333; padding: 16px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
              .text-zinc-500 { color: #888; font-family: monospace; }
              .text-white { color: #111 !important; }
              .bg-black .text-white { color: #fff !important; }
              .text-zinc-300 { color: #444 !important; }
              .bg-black .text-zinc-300 { color: #d4d4d8 !important; }
              .text-brand { color: #cc0000 !important; }
              .text-brand-light { color: #ff3333 !important; }
              .text-green-500 { color: #15803d !important; } /* Darker green for PDF */
              .text-yellow-500 { color: #b45309 !important; } /* Darker yellow for PDF */

              /* Terminal Premium Card Layout Utilities */
              .grid { display: grid !important; }
              .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
              .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
              .gap-2 { gap: 0.5rem !important; }
              .gap-3 { gap: 0.75rem !important; }
              .col-span-2 { grid-column: span 2 / span 2 !important; }
              .flex { display: flex !important; }
              .justify-between { justify-content: space-between !important; }
              .items-start { align-items: flex-start !important; }
              .items-center { align-items: center !important; }
              .w-full { width: 100% !important; }
              .relative { position: relative !important; }
              .overflow-hidden { overflow: hidden !important; }
              
              /* Padding & Margins */
              .p-3 { padding: 0.75rem !important; }
              .p-4 { padding: 1rem !important; }
              .pt-1 { padding-top: 0.25rem !important; }
              .pt-2 { padding-top: 0.5rem !important; }
              .mt-3 { margin-top: 0.75rem !important; }
              .mt-4 { margin-top: 1rem !important; }
              .mb-2 { margin-bottom: 0.5rem !important; }

              /* Borders & Backgrounds */
              .rounded-lg { border-radius: 0.5rem !important; }
              .border { border-width: 1px !important; border-style: solid !important; }
              .border-t { border-top-width: 1px !important; }
              .border-white\\/5 { border-color: #e5e7eb !important; } 
              .border-brand\\/30 { border-color: rgba(204, 0, 0, 0.4) !important; }
              .border-green-500\\/30 { border-color: rgba(34, 197, 94, 0.4) !important; }
              .border-yellow-500\\/30 { border-color: rgba(234, 179, 8, 0.4) !important; }
              .bg-green-500\\/5 { background-color: #f0fdf4 !important; }
              .bg-yellow-500\\/5 { background-color: #fefce8 !important; }
              .bg-brand\\/5 { background-color: #fef2f2 !important; }

              /* Typography */
              .text-right { text-align: right !important; }
              .uppercase { text-transform: uppercase !important; }
              .font-mono { font-family: monospace !important; }
              .font-bold { font-weight: bold !important; }
              .font-black { font-weight: 900 !important; }
              .text-xs { font-size: 0.75rem !important; }
              .text-sm { font-size: 0.875rem !important; }
              .text-\\[10px\\] { font-size: 10px !important; }
              .text-\\[11px\\] { font-size: 11px !important; }
              .text-\\[8px\\] { font-size: 8px !important; }
              .tracking-widest { letter-spacing: 0.1em !important; }
              .opacity-80 { opacity: 0.8 !important; }
              
              
              /* Premium Header */
              .report-header {
                border-bottom: 2px solid #000;
                margin-bottom: 30px;
                padding-bottom: 15px;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
              }
              .brand-title {
                margin: 0;
                font-size: 24px;
                font-weight: 900;
                text-transform: uppercase;
                letter-spacing: -0.03em;
              }
              .brand-tagline {
                font-size: 10px;
                color: #666;
                text-transform: uppercase;
                letter-spacing: 0.2em;
                margin-top: 4px;
                font-weight: 600;
              }
              .report-meta {
                text-align: right;
              }
              .report-meta h3 {
                margin: 0;
                font-size: 14px;
                font-family: monospace;
              }
              .report-meta p {
                margin: 4px 0 0 0;
                font-size: 9px;
                color: #666;
                font-family: monospace;
              }
              .badge {
                background: #000;
                color: #fff;
                padding: 2px 6px;
                border-radius: 2px;
                font-size: 10px;
                vertical-align: middle;
              }

              /* Premium Watermark */
              .watermark-container {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-35deg);
                opacity: 0.04; 
                z-index: -100;
                pointer-events: none;
                text-align: center;
                width: 150%;
              }
              .wm-main {
                font-size: 50px;
                font-weight: 900;
                letter-spacing: 0.1em;
                white-space: nowrap;
                color: #000;
              }
              .wm-sub {
                font-size: 25px;
                font-weight: 700;
                font-family: monospace;
                letter-spacing: 0.3em;
                color: #000;
                margin-top: -10px;
              }

              /* Copyright Footer */
              .footer {
                position: fixed;
                bottom: 0;
                left: 0;
                width: 100%;
                text-align: center;
                font-size: 9px;
                color: #888;
                border-top: 1px solid #eaeaea;
                padding-top: 10px;
                font-family: monospace;
                letter-spacing: 0.1em;
                background: #fff;
              }

              /* Chat History Styles */
              .flex-col { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
              .items-end { align-items: flex-end; }
              .items-start { align-items: flex-start; }
              .group\\/msg { margin-bottom: 20px; padding-bottom: 10px; border-bottom: 1px solid #eee; }
              .text-brand { color: #cc0000 !important; }
              .text-zinc-400 { color: #888; }
              .max-w-\\[85\\%\\] { max-width: 85%; }
            </style>
          </head>
          <body>
            <div class="watermark-container">
              <div class="wm-main">WORLD PRIME POST</div>
              <div class="wm-sub">WPP NEXUS</div>
            </div>

            <div class="report-header">
              <div>
                <h1 class="brand-title">WORLD PRIME POST</h1>
                <div class="brand-tagline">Decoding the Global Shift</div>
              </div>
              <div class="report-meta">
                <h3>WPP NEXUS <span class="badge">V2.0</span></h3>
                <p>SECURE TERMINAL EXPORT // ${new Date().toLocaleString()}</p>
              </div>
            </div>

            <div style="margin-bottom: 30px; padding: 12px; background: #f8f9fa; border-left: 4px solid #000;">
              <h4 style="margin: 0 0 5px 0; font-family: monospace; font-size: 12px; text-transform: uppercase;">Subject: ${title}</h4>
              <p style="margin: 0; font-size: 10px; color: #666; font-family: monospace;">Classification: CONFIDENTIAL / PROPRIETARY INTELLIGENCE</p>
            </div>

            ${clone.innerHTML}

            <div class="footer">
              &copy; ${new Date().getFullYear()} WORLD PRIME POST. ALL RIGHTS RESERVED. SECURE EXPORT VIA WPP NEXUS V2.0.
            </div>
          </body>
        </html>
      `);
      doc.close();
      
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 500);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            drag
            dragConstraints={{ 
              left: typeof window !== 'undefined' ? -window.innerWidth + 80 : -500, 
              right: 0, 
              top: typeof window !== 'undefined' ? -window.innerHeight + 80 : -500, 
              bottom: 0 
            }}
            dragElastic={0.1}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 w-16 h-16 bg-zinc-950 rounded-xl shadow-[0_0_30px_rgba(255,0,0,0.3)] cursor-grab active:cursor-grabbing z-50 flex items-center justify-center overflow-hidden border border-white/10 group"
            style={{ clipPath: 'polygon(20% 0%, 100% 0, 100% 80%, 80% 100%, 0 100%, 0% 20%)' }}
          >
            {/* Cyber Grid Background */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:4px_4px] opacity-30" />
            <div className="absolute inset-0 bg-brand/20 animate-pulse group-hover:bg-brand/40 transition-colors" />
            
            <div className="relative z-10 flex flex-col items-center justify-center">
              <span className="text-white font-mono font-bold text-lg tracking-tighter">NX</span>
              <div className="w-8 h-[2px] bg-brand mt-1 shadow-[0_0_10px_rgba(255,0,0,0.8)]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Mobile backdrop */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99] md:hidden"
              />
            )}

          <motion.div
            initial={isMobile ? { y: '100%' } : { opacity: 0, y: 20, scale: 0.95 }}
            animate={isMobile ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
            exit={isMobile ? { y: '100%' } : { opacity: 0, y: 20, scale: 0.95 }}
            transition={isMobile ? { type: 'spring', damping: 30, stiffness: 350 } : { type: 'spring', damping: 25, stiffness: 300 }}
            className={isMobile
              ? "fixed bottom-0 left-0 right-0 h-[92dvh] bg-zinc-950 shadow-[0_-20px_60px_rgba(0,0,0,0.9)] flex flex-col z-[100] overflow-hidden font-mono border-t border-white/10 rounded-t-2xl"
              : isExpanded 
                ? "fixed bottom-4 right-4 w-[80vw] h-[95vh] max-w-[1400px] bg-zinc-950 shadow-[0_0_100px_rgba(0,0,0,0.9)] flex flex-col z-[100] overflow-hidden font-mono border border-white/10 [clip-path:polygon(0_0,calc(100%-20px)_0,100%_20px,100%_100%,20px_100%,0_calc(100%-20px))]"
                : "fixed bottom-6 right-6 w-[450px] md:w-[500px] lg:w-[550px] h-[80vh] max-h-[850px] bg-zinc-950 shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col z-[100] overflow-hidden font-mono border border-white/10 [clip-path:polygon(0_0,calc(100%-20px)_0,100%_20px,100%_100%,20px_100%,0_calc(100%-20px))]"
            }
          >
            {/* Mobile Drag Handle */}
            {isMobile && (
              <div className="flex justify-center items-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1 bg-white/20 rounded-full" />
              </div>
            )}

            {/* Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

            {/* Terminal Header */}
            <div className="relative bg-zinc-900 border-b border-white/10 p-4 flex items-center justify-between">
              {/* Angled Accent Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-brand via-brand/50 to-transparent" />
              
              <div className="flex items-center gap-3 relative z-10">
                <button 
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  title="Toggle Sentinel Sidebar"
                  className="p-2 mr-1 text-zinc-500 hover:text-brand transition-colors border border-transparent hover:border-brand/30 bg-zinc-950/50 rounded-lg flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="w-10 h-10 bg-black border border-white/20 flex items-center justify-center relative overflow-hidden flex-shrink-0" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}>
                  <div className="absolute inset-0 bg-brand/20 animate-pulse" />
                  <span className="text-white font-bold tracking-tighter">NX</span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                    WPP Nexus <span className="px-1 py-0.5 bg-brand/20 text-brand text-[8px] border border-brand/30">v2.0</span>
                  </h3>
                  <p className="text-[10px] text-brand uppercase tracking-widest flex items-center gap-1 mt-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand shadow-[0_0_5px_rgba(255,0,0,0.8)] animate-pulse" />
                    SECURE TERMINAL
                  </p>
                </div>
              </div>

              {/* Global Market Clocks (Only in Expanded Mode) */}
              {isExpanded && currentTime && (
                <div className="hidden lg:flex items-center gap-8 relative z-10 mx-auto">
                  {[
                    { label: 'NY', tz: 'America/New_York' },
                    { label: 'LON', tz: 'Europe/London' },
                    { label: 'TOK', tz: 'Asia/Tokyo' },
                    { label: 'BOM', tz: 'Asia/Kolkata' }
                  ].map(clock => {
                    const time = new Intl.DateTimeFormat('en-GB', { timeZone: clock.tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(currentTime);
                    return (
                      <div key={clock.label} className="flex flex-col items-center">
                        <span className="text-[8px] text-zinc-500 font-bold uppercase tracking-widest">{clock.label}</span>
                        <span className="text-xs text-white font-mono font-bold tracking-wider">{time}</span>
                      </div>
                    );
                  })}
                  <div className="h-6 w-[1px] bg-white/10 mx-2" />
                  <div className="flex flex-col items-center">
                    <span className="text-[8px] text-brand font-bold uppercase tracking-widest">LATENCY</span>
                    <span className="text-xs text-brand font-mono font-bold tracking-wider animate-[pulse_2s_infinite]">12ms</span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 relative z-10">
                <button 
                  onClick={() => handlePrint('nexus-chat-container', 'Full Terminal Session History')}
                  title="Export Chat PDF"
                  className="pdf-download-btn p-1.5 text-zinc-500 hover:text-brand transition-colors border border-transparent hover:border-brand/30 bg-zinc-950/50"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                </button>
                <button 
                  onClick={handleClearChat}
                  title="Reboot Terminal"
                  className="p-1.5 text-zinc-500 hover:text-brand transition-colors border border-transparent hover:border-brand/30 bg-zinc-950/50"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button 
                  onClick={() => setIsExpanded(!isExpanded)}
                  title={isExpanded ? "Collapse Terminal" : "Expand Terminal"}
                  className="p-1.5 text-zinc-500 hover:text-white transition-colors border border-transparent hover:border-white/30 bg-zinc-950/50 hidden md:block"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isExpanded ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    )}
                  </svg>
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  title="Minimize Terminal"
                  className="p-1.5 text-zinc-500 hover:text-white transition-colors border border-transparent hover:border-white/30 bg-zinc-950/50"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                  </svg>
                </button>
                <button 
                  onClick={() => { handleClearChat(); setIsOpen(false); }}
                  title="Terminate Session"
                  className="p-1.5 text-zinc-500 hover:text-red-500 transition-colors border border-transparent hover:border-red-500/30 bg-zinc-950/50"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)' }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Main Content Area (Sidebar + Chat) */}
            <div className="flex flex-1 overflow-hidden relative bg-black/90">
              {/* Sentinel Sidebar */}
              <AnimatePresence>
                {(isSidebarOpen || !isMobile) && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: isSidebarOpen ? (isMobile ? '100%' : '240px') : 0, opacity: isSidebarOpen ? 1 : 0 }}
                    exit={{ width: 0, opacity: 0 }}
                    className="h-full border-r border-white/10 flex flex-col bg-zinc-950 flex-shrink-0 z-20"
                    style={{ position: isMobile ? 'absolute' : 'relative', left: 0, top: 0, bottom: 0 }}
                  >
                    <div className="p-4 flex flex-col h-full overflow-hidden w-full" style={{ width: isMobile ? '100%' : '240px' }}>
                      <button 
                        onClick={() => {
                          handleClearChat();
                          setCurrentSessionId(null);
                          if(isMobile) setIsSidebarOpen(false);
                        }}
                        className="w-full flex items-center justify-center gap-2 p-3 bg-brand/10 hover:bg-brand/20 border border-brand/30 text-brand rounded-lg transition-colors mb-4 flex-shrink-0"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        <span className="font-bold tracking-widest uppercase text-xs">New Chat</span>
                      </button>

                      <div className="relative mb-4 flex-shrink-0">
                        <svg className="w-4 h-4 absolute left-3 top-3 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        <input 
                          type="text" 
                          placeholder="Search chats..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-black border border-white/10 rounded-lg py-2 pl-9 pr-3 text-xs text-white focus:outline-none focus:border-brand/50 transition-colors"
                        />
                      </div>

                      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent pr-2 flex flex-col gap-2">
                        {!isSignedIn ? (
                          <div className="text-zinc-500 text-[10px] text-center mt-4">Sign in to sync your secure history.</div>
                        ) : vaultSessions.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase())).map((session) => (
                          <div key={session._id} className={`group relative p-3 rounded-lg border cursor-pointer transition-colors ${currentSessionId === session._id ? 'bg-brand/10 border-brand/30' : 'bg-black border-transparent hover:border-white/10'}`} onClick={() => loadSession(session)}>
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-[11px] text-white font-bold tracking-wide break-words max-w-[85%]">{session.title}</span>
                              {session.isPinned && <span className="text-[10px] text-brand ml-1">📌</span>}
                            </div>
                            <div className="text-[9px] text-zinc-500">{new Date(session._createdAt).toLocaleDateString()}</div>
                            
                            {/* Hover Actions */}
                            <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/90 backdrop-blur-sm flex items-center gap-1 p-1 rounded-md border border-white/10 shadow-lg z-10">
                              <button onClick={(e) => { e.stopPropagation(); handleVaultAction('pin', session._id, { isPinned: !session.isPinned }) }} title="Pin" className="p-1 hover:text-brand text-zinc-400 transition-colors"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg></button>
                              <button onClick={(e) => { e.stopPropagation(); const t = prompt('New title:', session.title); if(t) handleVaultAction('rename', session._id, { title: t }); }} title="Rename" className="p-1 hover:text-brand text-zinc-400 transition-colors"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                              <button onClick={(e) => { e.stopPropagation(); handleVaultAction('share', session._id); navigator.clipboard.writeText(window.location.origin + '/shared-nexus/' + session._id); alert('Share link copied to clipboard!'); }} title="Share" className="p-1 hover:text-green-500 text-zinc-400 transition-colors"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg></button>
                              <button onClick={(e) => { e.stopPropagation(); if(confirm('Delete permanently?')) { fetch('/api/nexus/vault/delete', { method: 'DELETE', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({sessionId: session._id}) }).then(() => fetchVaultSessions()); if(currentSessionId === session._id) {handleClearChat(); setCurrentSessionId(null);} } }} title="Delete" className="p-1 hover:text-red-500 text-zinc-400 transition-colors"><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Chat Area */}
              <div id="nexus-chat-container" className="flex-1 overflow-y-auto p-4 space-y-4 relative z-10 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                {messages.map((msg, idx) => {
                // Skip rendering the empty assistant message box while loading, since the Thought Stream handles it
                if (isLoading && idx === messages.length - 1 && msg.role === 'assistant' && msg.content === '') {
                  return null;
                }
                
                return (
                  <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} w-full group/msg`}>
                  
                  {/* Speaker Label & Timestamp */}
                  <div className={`text-[9px] uppercase tracking-widest mb-1 px-1 opacity-60 flex items-center gap-2 ${msg.role === 'user' ? 'text-zinc-400 justify-end w-full' : 'text-brand w-full'}`}>
                    {msg.role === 'user' ? (
                      <>
                        <span className="text-[8px] opacity-50">{msg.timestamp}</span>
                        <span>USER_INPUT</span>
                        <span className="w-1 h-1 bg-zinc-500 rounded-full" />
                      </>
                    ) : (
                      <>
                        <span className="w-1 h-1 bg-brand rounded-full shadow-[0_0_5px_rgba(255,0,0,0.8)]" /> 
                        <span>AGENT_NEXUS</span>
                        <span className="text-[8px] opacity-50 text-zinc-500">{msg.timestamp}</span>
                        
                        {/* Copy Button (only visible on hover or when copied) */}
                        <button 
                          onClick={() => handleCopy(msg.content, idx)}
                          className={`ml-auto mr-1 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-1 ${copiedIndex === idx ? 'text-green-500' : 'text-zinc-500 hover:text-white'}`}
                        >
                          {copiedIndex === idx ? (
                            <><span className="text-[8px]">COPIED</span><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg></>
                          ) : (
                            <><span className="text-[8px]">COPY</span><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg></>
                          )}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Message Box */}
                  <div 
                    className={`max-w-[90%] p-3 text-sm leading-relaxed ${
                      msg.role === 'user' 
                        ? 'bg-zinc-800 text-zinc-200 border-l border-zinc-600' 
                        : 'bg-black text-white border border-white/10 shadow-[inset_0_0_20px_rgba(255,255,255,0.02)]'
                    }`}
                    style={{
                      clipPath: msg.role === 'user' 
                        ? 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)'
                        : 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
                    }}
                  >
                    <div id={`nexus-msg-${idx}`} className="w-full">
                      {msg.file && msg.file.mimeType?.startsWith('image/') && (
                        <div className="mb-4 rounded-lg overflow-hidden border border-white/10 max-w-[300px]">
                          <img src={`data:${msg.file.mimeType};base64,${msg.file.data}`} alt="Attached" className="w-full h-auto" />
                        </div>
                      )}
                      {msg.file && msg.file.mimeType === 'application/pdf' && (
                        <div className="mb-4 flex items-center gap-2 p-2 bg-zinc-900/50 border border-white/10 rounded-lg w-fit">
                          <svg className="w-5 h-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                          <span className="text-xs font-mono text-zinc-300 truncate max-w-[200px]">{msg.file.name}</span>
                        </div>
                      )}
                      {(() => {
                        const cleanedContent = cleanMessageText(msg.content);
                        const jsonChartBlocks: any[] = [];
                        let textWithoutCharts = cleanedContent.replace(/```json_chart\n([\s\S]*?)(?:```|$)/g, (match, p1) => {
                          try {
                            jsonChartBlocks.push(JSON.parse(p1));
                          } catch (e) {
                            // Ignored during streaming
                          }
                          return '';
                        });

                        const tradeSignals: any[] = [];
                        const textFinal = textWithoutCharts.replace(/\[\[TRADE_SIGNAL\s+asset="([^"]+)"\s+action="([^"]+)"\s+rsi="([^"]+)"\s+volume="([^"]+)"\s+darkpool="([^"]+)"\s+whale="([^"]+)"\]\]/g, (match, asset, action, rsi, volume, darkpool, whale) => {
                          tradeSignals.push({ asset, action, rsi, volume, darkpool, whale });
                          return '';
                        });

                        return (
                          <>
                            <div className="prose prose-sm prose-invert prose-p:my-1 prose-a:text-brand prose-a:underline-offset-4 prose-strong:text-brand-light prose-ul:my-1 prose-li:my-0">
                              <ReactMarkdown
                                components={{
                                  code({ node, inline, className, children, ...props }: any) {
                                    return <code className={className} {...props}>{children}</code>;
                                  }
                                }}
                              >
                                {textFinal}
                              </ReactMarkdown>
                            </div>
                            
                            {jsonChartBlocks.length > 0 && (
                              <div className="mt-6 flex flex-col gap-6 w-full" style={{ perspective: '1000px' }}>
                                {jsonChartBlocks.map((config, i) => (
                                  <motion.div 
                                    key={i} 
                                    whileHover={{ scale: 1.01, rotateX: 2, rotateY: -1, translateZ: 10 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                    className="w-full flex-shrink-0 border border-white/10 bg-black rounded-xl p-4 relative overflow-hidden group shadow-[0_0_30px_rgba(0,0,0,0.5)] transform-gpu hover:shadow-[0_0_50px_rgba(255,0,0,0.3)] hover:border-brand/50 transition-all duration-300"
                                  >
                                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-brand via-red-500 to-transparent opacity-80" />
                                    <div className="absolute inset-0 bg-gradient-to-br from-brand/10 to-transparent opacity-20 pointer-events-none group-hover:opacity-40 transition-opacity" />
                                    <div className="absolute top-0 left-0 w-full h-[1px] bg-brand/50 blur-[1px] animate-[pulse_2s_ease-in-out_infinite] pointer-events-none" />
                                    
                                    <div className="relative z-10 flex items-center justify-between mb-4 pb-2 border-b border-white/10">
                                      <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-brand rounded-full animate-pulse shadow-[0_0_5px_rgba(255,0,0,0.8)]" />
                                        <span className="text-[10px] text-white font-bold tracking-widest uppercase font-mono">Market Data Feed {i + 1}</span>
                                      </div>
                                      <span className="text-[9px] text-zinc-500 font-mono tracking-widest hidden md:inline-block group-hover:text-brand/80 transition-colors">SECURE TERMINAL // V2.0</span>
                                    </div>
                                    
                                    <NexusChart config={config} />
                                  </motion.div>
                                ))}
                              </div>
                            )}

                            {tradeSignals.length > 0 && (
                              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                                {tradeSignals.map((signal, i) => {
                                  const isBuy = signal.action.includes('BUY');
                                  const colorClass = isBuy ? 'text-green-500' : (signal.action.includes('HOLD') ? 'text-yellow-500' : 'text-brand');
                                  const borderClass = isBuy ? 'border-green-500/30 hover:border-green-500' : (signal.action.includes('HOLD') ? 'border-yellow-500/30 hover:border-yellow-500' : 'border-brand/30 hover:border-brand');
                                  const bgClass = isBuy ? 'bg-green-500/5' : (signal.action.includes('HOLD') ? 'bg-yellow-500/5' : 'bg-brand/5');

                                  return (
                                    <motion.div 
                                      key={i}
                                      whileHover={{ scale: 1.02 }}
                                      className={`p-3 rounded-lg border ${borderClass} ${bgClass} backdrop-blur-md relative overflow-hidden group`}
                                    >
                                      {/* Glowing background blob */}
                                      <div className={`absolute top-0 right-0 w-16 h-16 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity ${isBuy ? 'bg-green-500' : (signal.action.includes('HOLD') ? 'bg-yellow-500' : 'bg-brand')}`} />
                                      
                                      <div className="relative z-10 flex justify-between items-start mb-2">
                                        <div>
                                          <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">ASSET</div>
                                          <div className="text-white font-bold text-sm">{signal.asset}</div>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">SIGNAL</div>
                                          <div className={`font-black tracking-widest text-xs ${colorClass} animate-pulse`}>
                                            {signal.action}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className="grid grid-cols-2 gap-2 mt-3 pt-2 border-t border-white/5 relative z-10">
                                        <div>
                                          <div className="text-[8px] text-zinc-500 font-mono uppercase">RSI</div>
                                          <div className="text-[11px] text-zinc-300 font-mono">{signal.rsi}</div>
                                        </div>
                                        <div>
                                          <div className="text-[8px] text-zinc-500 font-mono uppercase">VOLUME</div>
                                          <div className="text-[11px] text-zinc-300 font-mono">{signal.volume}</div>
                                        </div>
                                        <div className="col-span-2 pt-1">
                                          <div className="text-[8px] text-zinc-500 font-mono uppercase">DARK POOL LIQUIDITY</div>
                                          <div className="text-[10px] text-brand-light font-mono opacity-80">{signal.darkpool}</div>
                                        </div>
                                        <div className="col-span-2">
                                          <div className="text-[8px] text-zinc-500 font-mono uppercase">WHALE TRACKING</div>
                                          <div className="text-[10px] text-brand-light font-mono opacity-80">{signal.whale}</div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Feedback Form Trigger */}
                    {msg.role === 'assistant' && msg.content.includes('[[RENDER_FEEDBACK_FORM]]') && (
                      <FeedbackForm sessionId={currentSessionId || undefined} />
                    )}

                    {msg.role === 'assistant' && msg.content.length > 50 && (
                      <button onClick={() => handlePrint(`nexus-msg-${idx}`, msg.pdfTitle || 'Terminal Report')} className="pdf-download-btn mt-4 p-3 border border-brand/30 bg-brand/10 text-brand flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest hover:bg-brand/20 transition-colors w-full justify-center" style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg> 
                        Download {msg.pdfTitle || 'Terminal Report'} (PDF)
                      </button>
                    )}

                    {/* Premium History Timeline Rendering */}
                    {msg.historyData && msg.historyData.length > 0 && (
                        <div className="mt-4 border border-white/10 bg-black/50 rounded-lg overflow-hidden relative z-10 font-sans">
                          <div className="bg-zinc-900 px-4 py-2 border-b border-white/10 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-brand uppercase tracking-widest">Secure Ledger</span>
                            <span className="text-[9px] text-zinc-500 font-mono">{msg.historyData.length} records</span>
                          </div>
                          <div className="divide-y divide-white/5">
                            {msg.historyData.map((h: any, i: number) => (
                              <div key={i} className="p-4 hover:bg-white/5 transition-colors group">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-white uppercase tracking-wider">{h.type}</span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full border bg-zinc-900 font-mono tracking-wider" 
                                      style={{
                                        borderColor: h.status === 'closed' || h.status === 'resolved' || h.status === 'converted' ? 'rgba(34,197,94,0.3)' : 
                                                     h.status === 'in_progress' || h.status === 'pending' ? 'rgba(234,179,8,0.3)' : 'rgba(239,68,68,0.3)',
                                        color: h.status === 'closed' || h.status === 'resolved' || h.status === 'converted' ? '#4ade80' : 
                                               h.status === 'in_progress' || h.status === 'pending' ? '#facc15' : '#ef4444'
                                      }}
                                    >
                                      {h.status.toUpperCase()}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-zinc-500 font-mono">{new Date(h.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-mono bg-black/30 px-2 py-1 rounded inline-flex border border-white/5 group-hover:border-brand/20 transition-colors">
                                  <span className="text-brand">ID:</span> {h.ticketId}
                                </div>

                                {/* ── ADMIN REPLY BLOCK ─────────────────────── */}
                                {h.adminReply && (
                                  <div className="mt-3 border border-brand/20 bg-brand/5 relative overflow-hidden"
                                    style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
                                  >
                                    {/* Header */}
                                    <div className="flex items-center gap-2 px-3 py-2 border-b border-brand/20 bg-brand/10">
                                      <span className="w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
                                      <span className="text-brand text-[9px] font-black uppercase tracking-[0.25em]">
                                        Transmission Received — Admin Response
                                      </span>
                                    </div>
                                    {/* Message */}
                                    <div className="px-3 py-3">
                                      <p className="text-zinc-200 text-[11px] font-mono leading-relaxed whitespace-pre-wrap">
                                        {h.adminReply}
                                      </p>
                                    </div>
                                    {/* Decorative corner */}
                                    <div className="absolute bottom-0 right-0 w-6 h-6 border-t border-l border-brand/20" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {msg.historyData && msg.historyData.length === 0 && (
                        <div className="mt-4 p-4 border border-white/10 bg-black/50 text-center text-xs text-zinc-500 font-mono uppercase tracking-widest">
                          No historical records found for your account.
                        </div>
                      )}

                    {/* Elite Investor Data Room Protocol Trigger */}
                    {msg.isDataRoomUnlocked && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-4 p-4 border border-green-500/30 bg-green-500/5 relative overflow-hidden group/vault"
                        style={{ clipPath: 'polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                      >
                        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(34,197,94,0.05)_50%,transparent_75%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_2s_infinite]" />
                        <div className="flex items-center gap-2 mb-2 relative z-10">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-[ping_1.5s_infinite] shadow-[0_0_10px_rgba(34,197,94,0.8)]" />
                          <span className="text-green-500 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                            ACCESS GRANTED
                          </span>
                        </div>
                        <p className="text-[10px] text-green-500/70 mb-3 uppercase tracking-wide relative z-10 font-mono">Project Vanguard Data Room unlocked for verified profile.</p>
                        <a 
                          href="/institutional" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 bg-green-500/10 text-green-400 px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold hover:bg-green-500/20 transition-colors border border-green-500/30 relative z-10"
                          style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)' }}
                        >
                          ENTER SECURE VAULT
                        </a>
                      </motion.div>
                    )}

                    {/* Elite Tech Startup Telemetry (Latency & Tokens) */}
                    {msg.latency && msg.role === 'assistant' && (
                       <div className="mt-3 pt-2 border-t border-brand/10 flex flex-wrap items-center gap-3 text-[8px] text-zinc-500 uppercase tracking-widest font-mono">
                         <span className="flex items-center gap-1"><span className="w-1 h-1 bg-brand/50" /> LATENCY: {(msg.latency / 1000).toFixed(2)}s</span>
                         <span className="flex items-center gap-1"><span className="w-1 h-1 bg-brand/50" /> TOKENS: {Math.floor(msg.content.length / 4)}</span>
                         <span className="flex items-center gap-1 text-green-500/50"><span className="w-1 h-1 bg-green-500/50" /> SECURE_EXECUTION</span>
                       </div>
                    )}

                    {/* Proprietary Feature: WPP Global Sentinel Threat Scan */}
                    {msg.threatScan && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 border border-white/10 bg-black p-4 relative overflow-hidden" 
                        style={{ clipPath: 'polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                      >
                        {/* Threat Level Background Glow */}
                        <div className={`absolute top-0 right-0 w-32 h-32 blur-[50px] opacity-20 ${
                          msg.threatScan.level === 'CRITICAL' ? 'bg-red-600' :
                          msg.threatScan.level === 'HIGH' ? 'bg-orange-500' :
                          msg.threatScan.level === 'ELEVATED' ? 'bg-yellow-500' : 'bg-green-500'
                        }`} />
                        
                        <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-2 relative z-10">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-brand animate-[spin_4s_linear_infinite]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                            </svg>
                            <span className="text-white text-[10px] font-bold uppercase tracking-widest">WPP Global Sentinel</span>
                          </div>
                          <span className="text-[8px] bg-white/10 px-2 py-0.5 text-zinc-300 font-mono uppercase tracking-widest">PROPRIETARY ENGINE</span>
                        </div>

                        <div className="flex items-start justify-between relative z-10 gap-4">
                          <div>
                            <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Target Entity</span>
                            <h4 className="text-base text-white font-bold uppercase tracking-wide leading-tight mt-0.5">{msg.threatScan.target}</h4>
                          </div>
                          <div className="text-right">
                            <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Threat Level</span>
                            <div className={`px-3 py-1 mt-1 text-[10px] font-bold uppercase tracking-widest border ${
                               msg.threatScan.level === 'CRITICAL' ? 'bg-red-500/10 text-red-500 border-red-500/30' :
                               msg.threatScan.level === 'HIGH' ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' :
                               msg.threatScan.level === 'ELEVATED' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' : 'bg-green-500/10 text-green-500 border-green-500/30'
                            }`} style={{ clipPath: 'polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 0 100%)' }}>
                              {msg.threatScan.level}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-white/5 relative z-10">
                          <span className="text-[8px] text-zinc-500 uppercase tracking-widest mb-1 block">Predictive Summary</span>
                          <p className="text-[11px] text-zinc-300 font-mono leading-relaxed">{msg.threatScan.summary}</p>
                        </div>
                      </motion.div>
                    )}

                    {/* Support Ticket Status Tracker */}
                    {msg.ticketStatus && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 border border-zinc-800 bg-[#09090b] p-4 relative overflow-hidden" 
                        style={{ clipPath: 'polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                      >
                        <div className="flex items-center justify-between mb-4 border-b border-zinc-800 pb-2">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <span className="text-white text-[10px] font-bold uppercase tracking-widest">Support Ticket Status</span>
                          </div>
                          <span className={`text-[8px] px-2 py-0.5 font-bold uppercase tracking-widest ${
                            msg.ticketStatus.status === 'open' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' :
                            msg.ticketStatus.status === 'resolved' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-blue-500/10 text-blue-500 border border-blue-500/20'
                          }`}>{msg.ticketStatus.status.replace('_', ' ')}</span>
                        </div>
                        <p className="text-[12px] text-zinc-400 italic line-clamp-2 pl-3 border-l-2 border-brand/50">
                          "{msg.ticketStatus.issue}"
                        </p>
                      </motion.div>
                    )}


                    {/* Interactive UI: Market Options */}
                    {msg.role === 'assistant' && msg.content.includes('[[SHOW_MARKET_OPTIONS]]') && (
                      <div className="mt-4 flex flex-col gap-2">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">Select Category:</span>
                        <div className="grid grid-cols-2 gap-2">
                          {["Global Indices", "Forex — Major Pairs", "Crypto — Top Assets", "Global Economic Calendar"].map(opt => (
                            <button 
                              key={opt}
                              onClick={() => handleSubmit(undefined, `Show me live data for ${opt}`)}
                              className="text-xs bg-zinc-900 border border-white/10 hover:border-brand/50 p-2 text-left transition-colors"
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Interactive UI: Universal Protocol Options */}
                    {msg.role === 'assistant' && msg.content.match(/\[\[SHOW_PROTOCOL_OPTIONS protocol="(.*?)"\]\]/) && (
                      <div className="mt-4 flex flex-col gap-2">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{msg.content.match(/\[\[SHOW_PROTOCOL_OPTIONS protocol="(.*?)"\]\]/)?.[1]} PROTOCOL DASHBOARD:</span>
                        <div className="flex flex-col gap-2">
                          <button 
                            onClick={() => handleSubmit(undefined, "Fetch my secure request history and show live progress of my past tickets.")}
                            className="text-xs bg-zinc-900 border border-brand/30 hover:bg-brand/10 hover:border-brand text-brand p-3 transition-colors flex flex-col items-start gap-1 text-left relative overflow-hidden"
                          >
                            <div className="absolute top-0 right-0 w-16 h-16 bg-brand/10 blur-xl rounded-full" />
                            <span className="font-bold tracking-wide uppercase flex items-center gap-2 relative z-10">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              View My Request History
                            </span>
                            <span className="text-[10px] text-zinc-400 relative z-10">Track live progress and history of your past queries.</span>
                          </button>
                          <button 
                            onClick={() => handleSubmit(undefined, `I want to start a new ${msg.content.match(/\[\[SHOW_PROTOCOL_OPTIONS protocol="(.*?)"\]\]/)?.[1]} request.`)}
                            className="text-xs bg-zinc-900 border border-white/10 hover:bg-white/5 hover:border-white/30 text-white p-3 transition-colors flex flex-col items-start gap-1 text-left"
                          >
                            <span className="font-bold tracking-wide uppercase flex items-center gap-2">
                              ➕ Start New Request
                            </span>
                            <span className="text-[10px] text-zinc-400">Initiate a new ticket or inquiry for this protocol.</span>
                          </button>
                          {msg.content.match(/\[\[SHOW_PROTOCOL_OPTIONS protocol="(.*?)"\]\]/)?.[1] === 'Support' && (
                            <button 
                              onClick={() => handleSubmit(undefined, "I need Live AI Help right now instead of making a ticket.")}
                              className="text-xs bg-zinc-900 border border-white/10 hover:bg-white/5 hover:border-white/30 text-zinc-300 p-3 transition-colors flex flex-col items-start gap-1 text-left"
                            >
                              <span className="font-bold tracking-wide uppercase flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                                Live AI Help
                              </span>
                              <span className="text-[10px] text-zinc-500">Get instant AI answers for basic issues.</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}

              {/* Quick Actions Grid (Only show if chat is at welcome message) */}
              {messages.length === 1 && !isLoading && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 pt-4 border-t border-white/5"
                >
                  <div className="flex items-center justify-between mb-3 px-1">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">
                      AVAILABLE PROTOCOLS //
                    </span>
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_5px_rgba(34,197,94,0.6)]"></span>
                  </div>
                  
                  <div className="flex flex-col gap-1 relative z-10">
                    {/* Background glow for the list */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-brand/5 blur-3xl rounded-full pointer-events-none" />
                    
                    {quickActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => handleSubmit(undefined, action.query, true)}
                        className="group relative flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-all duration-300 text-left w-full border border-transparent hover:border-white/10 overflow-hidden"
                      >
                        {/* Orb hover effect inside button */}
                        <div className="absolute left-0 w-1 h-full bg-brand opacity-0 group-hover:opacity-100 transition-opacity rounded-l-lg" />
                        
                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-zinc-900 border border-white/5 group-hover:border-brand/30 group-hover:bg-brand/10 transition-colors z-10">
                          <span className="text-[12px] opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all">{action.icon}</span>
                        </div>
                        
                        <div className="flex flex-col z-10">
                          <span className="text-[12px] text-zinc-300 font-bold tracking-wide group-hover:text-brand transition-colors">
                            {action.title}
                          </span>
                          <span className="text-[10px] text-zinc-500 font-mono tracking-tight">
                            {action.desc}
                          </span>
                        </div>
                        
                        <div className="ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 z-10">
                          <svg className="w-4 h-4 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

               {/* Loading Indicator (AI Thought Stream) */}
              {isLoading && messages[messages.length - 1]?.role === 'assistant' && messages[messages.length - 1]?.content === '' && (
                 <div className="flex flex-col items-start w-full mt-4">
                    <div className="text-[9px] uppercase tracking-widest mb-1 px-1 opacity-60 text-brand flex items-center gap-1">
                      <span className="w-1 h-1 bg-brand rounded-full shadow-[0_0_5px_rgba(255,0,0,0.8)] animate-pulse" /> AGENT_NEXUS
                    </div>
                    <div className="bg-black/50 backdrop-blur-md border border-brand/30 p-4 px-5 max-w-[85%]" style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                      <div className="flex items-center gap-3">
                        <span className="w-2 h-4 bg-brand animate-[pulse_0.5s_infinite]" />
                        <span className="text-brand font-mono text-[10px] md:text-xs tracking-widest uppercase opacity-80">
                          {AI_THOUGHTS[currentThoughtIndex]}
                        </span>
                      </div>
                    </div>
                 </div>
              )}

              <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Command Input Area */}
            <div className="p-4 bg-zinc-900 border-t border-white/10 relative z-10">
              {/* Command Palette Popup */}
              <AnimatePresence>
                {showCommandPalette && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute bottom-full left-4 right-4 mb-2 bg-black border border-brand/30 shadow-[0_0_30px_rgba(255,0,0,0.2)] rounded-t-xl overflow-hidden z-20 font-mono"
                  >
                    <div className="px-3 py-2 bg-brand/10 border-b border-brand/20 text-[10px] text-brand font-bold tracking-widest uppercase flex items-center gap-2 flex-shrink-0">
                      <span className="w-1.5 h-1.5 bg-brand rounded-full animate-pulse" /> Hedge-Fund Command Palette
                    </div>
                    <div className="flex flex-col max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-brand/30 scrollbar-track-transparent">
                      {[
                        { cmd: '/pulse', desc: 'Join The Pulse: Global Intelligence Newsletter', query: "I want to subscribe to The Pulse newsletter to receive global intelligence updates." },
                        { cmd: '/mission', desc: 'Join Our Mission: Intelligence Bureau Recruitment', query: "I want to join your mission. Start the recruitment application protocol." },
                        { cmd: '/vanguard', desc: 'Project Vanguard: VC & Investor Data Room', query: "I represent an investment firm and would like to request access to the Project Vanguard Data Room." },
                        { cmd: '/api', desc: 'B2B API Solutions: Enterprise Data Feeds', query: "My company wants to integrate WPP Data Feeds and AI via API. How do we start?" },
                        { cmd: '/market', desc: 'Live Market Data: Real-time Asset Tracking', query: "Can I get the live market data and trend forecast?" },
                        { cmd: '/risk', desc: 'Global Risk Scan: Proprietary Threat Analysis', query: "Run a proprietary WPP Global Sentinel threat scan on the Middle East geopolitical situation." },
                        { cmd: '/history', desc: 'My Request History: Track Past Tickets & Inquiries', query: "Fetch the secure history of all my past requests, tickets, and institutional inquiries." },
                        { cmd: '/support', desc: 'Support Desk: Technical & Platform Help', query: "I need help. Can I raise a support ticket?" },
                        { cmd: '/institutional', desc: 'Institutional Relations: Founder & Business Escalation', query: "I want to contact the founder for Institutional Relations or business inquiries. Escalate this." },
                        { cmd: '/alpha', desc: 'Alpha Signal: Under-reported Market Trends', query: "Activate Alpha Signal Protocol. Scan internal WPP intelligence for under-reported, high-impact market trends." },
                        { cmd: '/sentinel', desc: 'Sentinel Matrix: T+36 Month Macro Forecast', query: "Initialize the Sentinel Predictive Matrix. Forecast global macroeconomic and technological shifts over the next 36 months to identify hidden Alpha." },
                        { cmd: '/clear', desc: 'Purge terminal memory', query: "" }
                      ].map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            if (item.cmd === '/clear') {
                              handleClearChat();
                              setInput('');
                            } else if (item.query) {
                              handleSubmit(undefined, item.query, true);
                            } else {
                              setInput(item.cmd + ' ');
                              textareaRef.current?.focus();
                            }
                            setShowCommandPalette(false);
                          }}
                          className="flex items-center justify-between px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0 text-left transition-colors group"
                        >
                          <span className="text-white font-bold text-xs group-hover:text-brand transition-colors">{item.cmd}</span>
                          <span className="text-zinc-500 text-[10px] hidden md:block">{item.desc}</span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={(e) => {
                e.preventDefault();
                setShowCommandPalette(false);
                handleSubmit(e);
              }} className="relative flex flex-col">
                
                {/* File Attachment Preview */}
                <AnimatePresence>
                  {attachedFile && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute bottom-full mb-2 left-0 z-10 flex items-center gap-2 p-2 bg-zinc-900 border border-brand/30 rounded-lg shadow-lg max-w-[200px]"
                    >
                      <div className="flex-1 truncate text-xs text-brand font-mono">
                        {attachedFile.name}
                      </div>
                      <button 
                        type="button" 
                        onClick={() => setAttachedFile(null)}
                        className="text-zinc-400 hover:text-white p-1"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>

                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) {
                      alert("File size exceeds 2MB limit.");
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const base64String = (event.target?.result as string).split(',')[1];
                      setAttachedFile({
                        data: base64String,
                        mimeType: file.type,
                        name: file.name
                      });
                    };
                    reader.readAsDataURL(file);
                    // Reset input so the same file can be selected again if removed
                    e.target.value = '';
                  }}
                />

                <div className="relative flex items-center">
                  {isSignedIn && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      title="Attach File or Image"
                      className="absolute left-3 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-brand transition-colors z-10"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    </button>
                  )}
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      const val = e.target.value;
                      setInput(val);
                      if (val.startsWith('/')) {
                        setShowCommandPalette(true);
                      } else {
                        setShowCommandPalette(false);
                      }
                    }}
                    onPaste={(e) => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (let i = 0; i < items.length; i++) {
                        if (items[i].type.indexOf('image') !== -1) {
                          const file = items[i].getAsFile();
                          if (!file) continue;
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const base64String = (event.target?.result as string).split(',')[1];
                            setAttachedFile({
                              data: base64String,
                              mimeType: file.type,
                              name: 'Pasted Image'
                            });
                          };
                          reader.readAsDataURL(file);
                          e.preventDefault(); // Prevent pasting the image name as text
                          break; // Only handle the first image
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        // Desktop: Enter sends, Shift+Enter new line. Mobile: Enter new line.
                        if (!isMobile) {
                          e.preventDefault();
                          if ((input.trim() || attachedFile) && !isLoading && isSignedIn) {
                            handleSubmit(e);
                          }
                        }
                      }
                    }}
                    placeholder={isSignedIn ? "Enter command, query, or paste image..." : "Please LOG IN to access terminal..."}
                    disabled={isLoading || !isSignedIn}
                    rows={1}
                    className="w-full bg-black border border-white/10 text-white pl-10 pr-24 py-3 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/50 transition-all disabled:opacity-50 placeholder:text-zinc-600 font-mono resize-none scrollbar-thin scrollbar-thumb-white/10"
                    style={{ 
                      clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                      minHeight: '46px',
                      maxHeight: '150px'
                    }}
                  />
                  {isSignedIn && (
                    <button
                      type="button"
                      onClick={handleVoiceInput}
                      title="Jarvis Voice Mode"
                      className={`absolute right-[65px] top-1/2 -translate-y-1/2 p-2 rounded-full transition-all ${
                        isListening ? 'text-brand animate-pulse bg-brand/10 shadow-[0_0_15px_rgba(255,0,0,0.5)]' : 'text-zinc-500 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                    </button>
                  )}
                  {isSignedIn ? (
                    <button
                      type="submit"
                      disabled={isLoading || !input.trim()}
                      className="absolute right-0 top-0 bottom-0 bg-brand hover:bg-brand-light text-white px-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </button>
                  ) : (
                    <a
                      href="/sign-in"
                      className="absolute right-0 top-0 bottom-0 bg-zinc-800 hover:bg-zinc-700 text-white px-5 transition-colors flex items-center justify-center font-bold text-[10px] tracking-widest uppercase"
                      style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 15px 100%, 0 calc(100% - 15px))' }}
                    >
                      LOG IN
                    </a>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="encrypted" checked readOnly className="w-2.5 h-2.5 bg-black border border-brand/50 rounded-sm appearance-none checked:bg-brand" />
                    <label htmlFor="encrypted" className="text-[8px] text-zinc-500 uppercase tracking-widest font-mono cursor-pointer hover:text-white transition-colors">
                      {isSignedIn ? 'ENCRYPTED CONNECTION' : 'AUTHENTICATION REQUIRED'}
                    </label>
                  </div>
                  <span className="text-[8px] text-brand/50 uppercase tracking-widest font-mono">WORLDPRIMEPOST // TERMINAL</span>
                </div>
              </form>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
